import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ProceduresService } from '../../../../core/services/procedures.service';

@Component({
  selector: 'app-add-procedure-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div class="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" (click)="close.emit()"></div>

      <!-- Modal Content -->
      <div class="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-slide-up flex flex-col max-h-[90vh]">
        <!-- Header -->
        <div class="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
          <div>
            <h3 class="text-xl font-black text-slate-800 uppercase tracking-widest" id="modal-title">
              {{ 'modal_add_procedures_title' | translate }}
            </h3>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{{ 'modal_add_procedures_subtitle' | translate }}</p>
          </div>
          <button (click)="close.emit()" class="p-2 hover:bg-slate-50 rounded-xl transition-colors text-slate-400 hover:text-slate-600">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto p-8">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <!-- Step 1 -->
            <a 
              href="https://pkqviipqnvoaxmahlblq.supabase.co/storage/v1/object/public/procedures//Example.xlsx"
              target="_blank"
              class="block p-0 rounded-2xl transition hover:scale-[1.02] cursor-pointer text-center relative overflow-hidden bg-slate-50 border border-slate-100 flex items-center justify-center h-44 shadow-sm"
            >
              <img src="/assets/images/Frame_1000004871.png" alt="Step 1" class="max-w-full max-h-full object-contain">
            </a>

            <!-- Step 2 -->
            <div class="block p-0 rounded-2xl text-center relative overflow-hidden bg-slate-50 border border-slate-100 flex items-center justify-center h-44 shadow-sm">
              <img src="/assets/images/Frame_1000004872.png" alt="Step 2" class="max-w-full max-h-full object-contain">
            </div>

            <!-- Step 3 -->
            <div class="block p-0 rounded-2xl text-center relative overflow-hidden bg-slate-50 border border-slate-100 flex items-center justify-center h-44 shadow-sm">
               <img src="/assets/images/Frame_1000004873.png" alt="Step 3" class="max-w-full max-h-full object-contain">
            </div>
          </div>

          <!-- Upload Area -->
          <div 
            class="border-4 border-dashed border-slate-100 rounded-3xl p-16 text-center hover:border-primary/30 hover:bg-primary/5 transition-all cursor-pointer relative bg-slate-50/50 group"
            (drop)="onDrop($event)" 
            (dragover)="onDragOver($event)"
            (click)="fileInput.click()"
          >
              <input 
                type="file" 
                #fileInput 
                class="hidden" 
                accept=".xlsx, .xls"
                (change)="onFileSelected($event)"
              >
              
              <div *ngIf="!loading && !uploadSuccess" class="animate-fadeIn">
                 <div class="flex justify-center mb-6">
                    <div class="w-16 h-16 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                      <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    </div>
                 </div>
                 <p class="text-lg text-slate-800 font-black uppercase tracking-widest">{{ 'modal_add_procedures_dropzone' | translate }}</p>
                 <p class="text-xs text-slate-400 font-bold uppercase tracking-widest mt-2">{{ 'modal_add_procedures_dropzone_hint' | translate }}</p>
              </div>

              <!-- Loading State -->
              <div *ngIf="loading" class="flex flex-col items-center justify-center animate-fadeIn">
                <div class="relative w-16 h-16 mb-6">
                  <div class="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                  <div class="absolute inset-0 border-4 border-t-primary rounded-full animate-spin"></div>
                </div>
                <p class="text-slate-800 font-black uppercase tracking-widest text-lg">{{ 'modal_add_procedures_loading' | translate }}</p>
              </div>

              <!-- Success State -->
              <div *ngIf="uploadSuccess" class="flex flex-col items-center justify-center animate-fadeIn text-center">
                 <div class="w-16 h-16 rounded-full bg-success/10 border border-success/20 flex items-center justify-center text-success mb-6">
                   <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                   </svg>
                 </div>
                 <p class="text-success font-black uppercase tracking-widest text-lg">{{ customSuccessMessage || ('modal_add_procedures_success' | translate) }}</p>
              </div>

              <!-- Error State -->
              <div *ngIf="errorMessage" class="flex flex-col items-center justify-center animate-fadeIn text-center">
                 <div class="w-16 h-16 rounded-full bg-danger/10 border border-danger/20 flex items-center justify-center text-danger mb-6">
                   <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"></path>
                   </svg>
                 </div>
                 <p class="text-danger font-black uppercase tracking-widest text-lg">{{ errorMessage }}</p>
                 <button (click)="errorMessage = null" class="mt-6 px-6 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors">
                    {{ 'button_back' | translate }}
                 </button>
              </div>
          </div>
        </div>
      </div>
    </div>
  `
})
export class AddProcedureModal {
  @Input() branchId: number | null = null;
  @Output() close = new EventEmitter<boolean>(); // Emit true if success

  loading = false;
  uploadSuccess = false;
  errorMessage: string | null = null;
  customSuccessMessage: string | null = null;

  constructor(
    private proceduresService: ProceduresService,
    private translate: TranslateService,
  ) { }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.handleFile(file);
    }
  }

  async handleFile(file: File) {
    if (!this.branchId) {
      console.error('No branch ID available');
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    this.uploadSuccess = false;

    try {
      const result = await this.proceduresService.uploadProcedures(file, this.branchId);
      
      // Handle n8n response format (often an array)
      const responseData = Array.isArray(result) ? result[0] : result;

      // n8n has answered in three different shapes over time, so this reads all
      // three rather than pinning to the newest:
      //
      //   current  ok    -> { success: true,  message, rows }
      //            error -> { success: false, message, code, details, hint, raw_error }
      //   previous ok    -> { message, import_id, rows }                 (no success flag)
      //            error -> { message, supabase_code, supabase_message, supabase_hint, raw_error }
      //
      // `success` is now sent on BOTH branches, so when it is a boolean it is
      // authoritative and nothing else needs guessing. Getting this wrong has
      // bitten twice already - once a successful import fell into the else
      // branch and showed its own "imported successfully" text as the failure
      // reason - which is why the fallback below never trusts `message`.
      const hasErrorDetail =
        !!responseData?.code ||
        !!responseData?.details ||
        !!responseData?.supabase_code ||
        !!responseData?.supabase_message ||
        !!responseData?.raw_error;
      const succeeded =
        typeof responseData?.success === 'boolean'
          ? responseData.success
          : !hasErrorDetail && !!responseData?.import_id;

      if (succeeded) {
        this.uploadSuccess = true;
        this.customSuccessMessage =
          responseData.rows != null
            ? this.translate.instant('modal_add_procedures_success_rows', { count: responseData.rows })
            : responseData.message || null;

        setTimeout(() => {
          this.close.emit(true);
        }, 3000);
      } else {
        // `details`/`hint` (previously supabase_message/supabase_hint) say WHY it
        // failed; `message` is only ever the generic "Import failed", which on
        // its own is undiagnosable.
        const detail = [
          responseData?.details ?? responseData?.supabase_message,
          responseData?.hint ?? responseData?.supabase_hint,
        ]
          .filter(Boolean)
          .join(' — ');
        this.errorMessage =
          [responseData?.message, detail].filter(Boolean).join(' ').trim() ||
          this.translate.instant('modal_add_procedures_error_generic');
        if (responseData?.code || responseData?.supabase_code || responseData?.raw_error) {
          console.error('[ImportProcedures] n8n reported a failure', responseData);
        }
        this.loading = false;
      }
    } catch (error: any) {
      console.error('Upload failed', error);
      this.errorMessage = error.message || 'Falha no upload do arquivo.';
      this.loading = false;
    }
  }
}
